import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAgent } from "../../Context/AgentContext";

const MyListingsPage = () => {
  const navigate = useNavigate();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const {
    agent,
    listings,
    loading,
    error,
    editListing,
    refreshListings,
    clearError,
    logoutAgent
  } = useAgent();

  const [selectedListing, setSelectedListing] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('newest');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0
  });

  useEffect(() => {
    // Calculate statistics from listings - EXACT same logic as AgentDashboard
    if (listings && listings.length > 0) {
      setStats({
        total: listings.length,
        approved: listings.filter(l => l.status === 'approved' || (!l.underCheck && l.status !== 'pending_approval')).length,
        pending: listings.filter(l => l.status === 'pending_approval' || l.underCheck).length,
        rejected: listings.filter(l => l.status === 'rejected').length
      });
    } else {
      // Reset stats if no listings
      setStats({
        total: 0,
        approved: 0,
        pending: 0,
        rejected: 0
      });
    }
  }, [listings]);

  const handleEditListing = (listing) => {
    setSelectedListing({ ...listing });
    setShowEditModal(true);
  };

  const handlePreviewListing = (listing) => {
    navigate(`/individual-akiya/${listing.property_id}`);
  };

  const handleUpdateListing = async (updates) => {
    try {
      clearError();
      await editListing(selectedListing.property_id, updates);

      setShowEditModal(false);
      setSelectedListing(null);

      // Show success message
      alert('Listing updated successfully! It will be reviewed by admin again.');
    } catch (error) {
      console.error('Failed to update listing:', error);
    }
  };

  const handleLogout = () => {
    logoutAgent();
    navigate('/');
  };

  const formatPrice = (price) => {
    if (!price) return 'Contact for Price';
    const numericPrice = price.toString().replace(/[^\d]/g, '');
    if (!numericPrice) return price;
    return `¥${parseInt(numericPrice).toLocaleString()}`;
  };

  const getStatusColor = (listing) => {
    if (listing.status === 'approved' || (!listing.underCheck && listing.status !== 'pending_approval')) {
      return 'approved-status';
    } else if (listing.status === 'rejected') {
      return 'rejected-status';
    }
    return 'pending-status';
  };

  const getStatusText = (listing) => {
    if (listing.status === 'approved' || (!listing.underCheck && listing.status !== 'pending_approval')) {
      return 'Approved';
    } else if (listing.status === 'rejected') {
      return 'Rejected';
    }
    return 'Pending Review';
  };

  const getStatusIcon = (listing) => {
    if (listing.status === 'approved' || (!listing.underCheck && listing.status !== 'pending_approval')) {
      return 'fas fa-check-circle';
    } else if (listing.status === 'rejected') {
      return 'fas fa-times-circle';
    }
    return 'fas fa-clock';
  };

  const filteredListings = listings.filter(listing => {
    // Filter by status
    if (activeFilter === 'approved' && !(listing.status === 'approved' || (!listing.underCheck && listing.status !== 'pending_approval'))) {
      return false;
    }
    if (activeFilter === 'pending' && !(listing.status === 'pending_approval' || listing.underCheck)) {
      return false;
    }
    if (activeFilter === 'rejected' && listing.status !== 'rejected') {
      return false;
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        listing.Address?.toLowerCase().includes(search) ||
        listing.Prefecture?.toLowerCase().includes(search) ||
        listing.Municipality?.toLowerCase().includes(search) ||
        listing.Room_Type?.toLowerCase().includes(search) ||
        listing.description?.toLowerCase().includes(search)
      );
    }

    return true;
  });

  const sortedListings = [...filteredListings].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt || b.created_at || b.dateAdded || 0) - new Date(a.createdAt || a.created_at || a.dateAdded || 0);
      case 'oldest':
        return new Date(a.createdAt || a.created_at || a.dateAdded || 0) - new Date(b.createdAt || b.created_at || b.dateAdded || 0);
      case 'price-high':
        return (parseFloat(b.Price?.replace(/[^\d]/g, '') || 0)) - (parseFloat(a.Price?.replace(/[^\d]/g, '') || 0));
      case 'price-low':
        return (parseFloat(a.Price?.replace(/[^\d]/g, '') || 0)) - (parseFloat(b.Price?.replace(/[^\d]/g, '') || 0));
      default:
        return 0;
    }
  });

  // Enhanced refresh function with loading state
  const handleRefresh = async () => {
    if (!refreshListings || typeof refreshListings !== 'function') {
      console.error('refreshListings function is not available');
      return;
    }

    setIsRefreshing(true);
    try {
      await refreshListings();
      console.log('Listings refreshed successfully');
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  if (loading && !isRefreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-grey)' }}>
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center animate-pulse"
            style={{ background: 'linear-gradient(to bottom right, var(--medium-sea-green), var(--sea-green))' }}>
            <i className="fas fa-building text-white text-2xl"></i>
          </div>
          <p className="light-header-nospacing">Loading your listings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-grey)' }}>
      {/* FontAwesome CDN */}
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
      />

      {/* Custom Styles */}
      <style>{`
        .approved-status {
          color: var(--sea-green);
          background-color: rgba(14, 177, 169, 0.1);
          border: 1px solid rgba(14, 177, 169, 0.2);
        }
        .pending-status {
          color: var(--dark-grey);
          background-color: rgba(148, 153, 149, 0.1);
          border: 1px solid rgba(148, 153, 149, 0.2);
        }
        .rejected-status {
          color: #dc2626;
          background-color: rgba(220, 38, 38, 0.1);
          border: 1px solid rgba(220, 38, 38, 0.2);
        }
        .featured-status {
          background: linear-gradient(to right, var(--medium-sea-green), var(--sea-green));
        }
        .teal-border {
          border-color: rgba(14, 177, 169, 0.2);
        }
        .teal-bg {
          background-color: rgba(14, 177, 169, 0.05);
        }
        .teal-hover:hover {
          background-color: rgba(14, 177, 169, 0.1);
        }
        .gradient-primary {
          background: linear-gradient(to right, var(--medium-sea-green), var(--sea-green));
        }
        .gradient-primary:hover {
          background: linear-gradient(to right, var(--sea-green), #0da399);
        }
        .text-primary {
          color: var(--medium-sea-green);
        }
        .text-secondary {
          color: var(--dark-grey);
        }
        .border-primary {
          border-color: rgba(16, 201, 192, 0.2);
        }
        .filter-active {
          background: var(--medium-sea-green);
          color: white;
        }
        .filter-inactive {
          color: var(--dark-grey);
        }
        .filter-inactive:hover {
          color: var(--color-2);
        }
        .view-mode-active {
          background: white;
          color: var(--medium-sea-green);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .view-mode-inactive {
          color: var(--dark-grey);
        }
        .form-input {
          border: 1px solid rgba(148, 153, 149, 0.3);
        }
        .form-input:focus {
          outline: none;
          border-color: var(--medium-sea-green);
          box-shadow: 0 0 0 2px rgba(16, 201, 192, 0.2);
        }
        .refresh-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header - CONSISTENT WITH DASHBOARD */}
      <header className="bg-[--medium-sea-green] shadow-sm border-b teal-border">
        <div className="f-section mx-auto" style={{ padding: '0 clamp(6px, 4vw, 30px)' }}>
          {/* Top Header Bar */}
          <div className="h-20 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* <button 
                onClick={() => navigate('/agent/dashboard')}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-colors"
              >
                <i className="fas fa-arrow-left text-white"></i>
              </button> */}
              {/* <div className="h-6 border-l" style={{ borderColor: 'rgba(255, 255, 255, 0.3)' }}></div> */}


              <h2
                className="m-0 font-bold text-white" // Fixed font weight
                style={{
                  fontSize: 'clamp(12px, 2.5vmin, 24px)', // Responds to both width and height
                  paddingTop: 'clamp(0.25rem, 1vmin, 0.5rem)'
                }}
              >
                My Listings
              </h2>
              {error && (
                <div className="flex items-center space-x-2">
                  <i className="fas fa-exclamation-triangle text-white opacity-70"></i>
                  <button
                    onClick={handleRefresh}
                    className="text-sm text-white hover:opacity-70 underline non-selectable"
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? 'Retrying...' : 'Retry Loading'}
                  </button>
                </div>
              )}
            </div>

            {/* Agent Profile Dropdown */}

          </div>

          {/* Page Title Section - CONSISTENT WITH DASHBOARD */}
          <div className="pb-8 -mt-4">
            <div className="flex items-center justify-between">
              <h1
                className="m-0 font-bold text-white"
                style={{
                  fontSize: 'clamp(24px, 8vmin, 72px)', // Responds to both width and height
                  lineHeight: 'clamp(28px, 9vmin, 78px)', // Proportional line height
                  paddingTop: 'clamp(0.25rem, 1vmin, 0.5rem)',
                  paddingBottom: 'clamp(0.25rem, 1vmin, 0.5rem)'
                }}
              >
                Manage Your Properties
              </h1>


              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-colors non-selectable"
                disabled={isRefreshing}
              >
                <i className={`fas fa-sync-alt mr-2 ${isRefreshing ? 'refresh-spin' : ''}`}></i>
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Error Display */}
      {error && (
        <div className="f-section mx-auto py-4" style={{ padding: '16px clamp(6px, 4vw, 30px)' }}>
          <div className="teal-bg border border-primary rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <i className="fas fa-exclamation-triangle text-primary mr-2"></i>
              <span className="light-header-nospacing text-primary" style={{ margin: 0 }}>{error}</span>
            </div>
            <button
              onClick={clearError}
              className="text-primary hover:opacity-70 non-selectable"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="f-section mx-auto py-6" style={{ padding: '24px clamp(6px, 4vw, 30px)' }}>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Listings */}
          <div className="bg-white p-6 rounded-xl shadow-sm border-primary border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary">Total Listings</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-2)' }}>{stats.total}</p>
                <p className="text-xs text-secondary mt-1">All your properties</p>
              </div>
              <div className="w-12 h-12 teal-bg rounded-lg flex items-center justify-center">
                <i className="fas fa-building text-primary text-xl"></i>
              </div>
            </div>
          </div>

          {/* Approved Listings */}
          <div className="bg-white p-6 rounded-xl shadow-sm border-primary border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary">Approved</p>
                <p className="text-2xl font-bold text-primary">{stats.approved}</p>
                <p className="text-xs text-secondary mt-1">Live on platform</p>
              </div>
              <div className="w-12 h-12 teal-bg rounded-lg flex items-center justify-center">
                <i className="fas fa-check-circle text-primary text-xl"></i>
              </div>
            </div>
          </div>

          {/* Pending Approval */}
          <div className="bg-white p-6 rounded-xl shadow-sm border-primary border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary">Pending</p>
                <p className="text-2xl font-bold text-secondary">{stats.pending}</p>
                <p className="text-xs text-secondary mt-1">Under review</p>
              </div>
              <div className="w-12 h-12 teal-bg rounded-lg flex items-center justify-center">
                <i className="fas fa-clock text-primary text-xl"></i>
              </div>
            </div>
          </div>

          {/* Rejected Listings */}
          <div className="bg-white p-6 rounded-xl shadow-sm border-primary border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary">Rejected</p>
                <p className="text-2xl font-bold" style={{ color: '#dc2626' }}>{stats.rejected}</p>
                <p className="text-xs text-secondary mt-1">Need revision</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}>
                <i className="fas fa-times-circle text-xl" style={{ color: '#dc2626' }}></i>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm border-primary border p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Filters */}
            <div className="flex items-center space-x-4">
              <div className="flex teal-bg rounded-lg p-1">
                {[
                  { id: 'all', label: 'All', count: stats.total, icon: 'fas fa-list' },
                  { id: 'approved', label: 'Approved', count: stats.approved, icon: 'fas fa-check-circle' },
                  { id: 'pending', label: 'Pending', count: stats.pending, icon: 'fas fa-clock' },
                  { id: 'rejected', label: 'Rejected', count: stats.rejected, icon: 'fas fa-times-circle' }
                ].map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setActiveFilter(filter.id)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all non-selectable ${activeFilter === filter.id
                      ? 'filter-active shadow-sm'
                      : 'filter-inactive'
                      }`}
                  >
                    <i className={`${filter.icon} mr-2`}></i>
                    {filter.label} ({filter.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Search and View Controls */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 form-input rounded-lg w-64"
                  placeholder="Search listings..."
                />
                <i className="fas fa-search absolute left-3 top-3 text-secondary"></i>
              </div>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 form-input rounded-lg"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price-high">Price: High to Low</option>
                <option value="price-low">Price: Low to High</option>
              </select>

              <div className="flex teal-bg rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-all non-selectable ${viewMode === 'grid' ? 'view-mode-active' : 'view-mode-inactive'
                    }`}
                >
                  <i className="fas fa-th-large"></i>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-all non-selectable ${viewMode === 'list' ? 'view-mode-active' : 'view-mode-inactive'
                    }`}
                >
                  <i className="fas fa-list"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Listings Display */}
        {sortedListings.length > 0 ? (
          <div className={viewMode === 'grid'
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "space-y-4"
          }>
            {sortedListings.map((listing) => (
              <div
                key={listing.property_id}
                className={`bg-white rounded-xl shadow-sm border-primary border overflow-hidden hover:shadow-md transition-all duration-300 ${viewMode === 'list' ? 'flex' : ''
                  }`}
              >
                {/* Property Image */}
                <div className={`relative ${viewMode === 'list' ? 'w-64 flex-shrink-0' : 'h-48'}`}>
                  {(listing.Images || listing.images) && (listing.Images || listing.images).length > 0 ? (
                    <img
                      src={(listing.Images || listing.images)[0]}
                      alt={listing.Address}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full teal-bg flex items-center justify-center">
                      <i className="fas fa-home text-primary text-3xl opacity-50"></i>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className={`absolute top-3 left-3 px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(listing)}`}>
                    <i className={`${getStatusIcon(listing)} mr-1`}></i>
                    {getStatusText(listing)}
                  </div>

                  {/* Featured Badge */}
                  {listing.featured && (
                    <div className="absolute top-3 right-3 featured-status text-white px-2 py-1 rounded-lg text-xs font-medium">
                      <i className="fas fa-star mr-1"></i>
                      Featured
                    </div>
                  )}
                </div>

                {/* Property Details */}
                <div className={`p-6 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold mb-1 line-clamp-1" style={{ color: 'var(--color-2)' }}>
                        {listing.Address}
                      </h3>
                      <p className="text-sm text-secondary">
                        {listing.Prefecture} • {listing.Municipality}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-primary">
                        {formatPrice(listing.Price)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div className="flex items-center text-secondary">
                      <i className="fas fa-home mr-2 w-4"></i>
                      <span>{listing.Room_Type || 'N/A'}</span>
                    </div>
                    <div className="flex items-center text-secondary">
                      <i className="fas fa-calendar mr-2 w-4"></i>
                      <span>{listing.Year_of_Construction || 'N/A'}</span>
                    </div>
                    <div className="flex items-center text-secondary">
                      <i className="fas fa-expand-arrows-alt mr-2 w-4"></i>
                      <span>{listing.Area_of_House || 'N/A'}</span>
                    </div>
                    <div className="flex items-center text-secondary">
                      <i className="fas fa-layer-group mr-2 w-4"></i>
                      <span>{listing.No_of_Floors ? `${listing.No_of_Floors} floors` : 'N/A'}</span>
                    </div>
                    <div className="flex items-center text-secondary">
                      <i className="fas fa-eye mr-2 w-4"></i>
                      <span>{listing.views || 0} views</span>
                    </div>
                  </div>

                  {listing.description && (
                    <p className="text-secondary text-sm mb-4 line-clamp-2">
                      {listing.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-secondary">
                      Added: {new Date(listing.createdAt || listing.created_at || listing.dateAdded).toLocaleDateString()}
                    </div>
                    <button
                      onClick={() => handlePreviewListing(listing)}
                      className="px-4 py-2 gradient-primary text-white rounded-lg hover:opacity-90 transition-colors text-sm non-selectable"
                    >
                      <i className="fas fa-search mr-2"></i>
                      Preview
                    </button>

                    <button
                      onClick={() => handleEditListing(listing)}
                      className="px-4 py-2 gradient-primary text-white rounded-lg hover:opacity-90 transition-colors text-sm non-selectable"
                    >
                      <i className="fas fa-edit mr-2"></i>
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 teal-bg rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-home text-primary text-2xl opacity-50"></i>
            </div>
            <h3 className="dark-header mb-2" style={{ color: 'var(--color-2)' }}>
              {searchTerm || activeFilter !== 'all' ? 'No listings found' : 'No listings yet'}
            </h3>
            <p className="light-header mb-6">
              {searchTerm || activeFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Create your first listing to get started'
              }
            </p>
            {(!searchTerm && activeFilter === 'all') && (
              <button
                onClick={() => navigate('/agent/add-listing')}
                className="px-6 py-3 gradient-primary text-white rounded-lg hover:opacity-90 transition-colors non-selectable"
              >
                <i className="fas fa-plus mr-2"></i>
                Add Your First Listing
              </button>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedListing && (
        <EditListingModal
          listing={selectedListing}
          onClose={() => {
            setShowEditModal(false);
            setSelectedListing(null);
          }}
          onUpdate={handleUpdateListing}
        />
      )}
    </div>
  );
};

// Edit Listing Modal Component
// Edit Listing Modal Component - Updated with separate image handling
const EditListingModal = ({ listing, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    // Basic Property Info
    Address: listing.Address || '',
    Prefecture: listing.Prefecture || '',
    Municipality: listing.Municipality || '',
    Price: listing.Price || '',
    Room_Type: listing.Room_Type || '',
    Year_of_Construction: listing.Year_of_Construction || '',
    Area_of_House: listing.Area_of_House || '',
    Area_of_Land: listing.Area_of_Land || '',
    No_of_Floors: listing.No_of_Floors || '',
    Empty_or_Not: listing.Empty_or_Not || 'Sale',

    // Contact & Description
    Contact_Info: listing.Contact_Info || '',
    description: listing.description || '',
    Link_to_Website: listing.Link_to_Website || '',

    // Images AND Coordinates (same as AkiyaTweak)
    Images: listing.Images || [],
    Coordinates: listing.Coordinates || [35.6762, 139.6503] // Default to Tokyo
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const roomTypes = [
    '1R', '1K', '1DK', '1LDK', '2K', '2DK', '2LDK', '3K', '3DK', '3LDK',
    '4K', '4DK', '4LDK', '5K', '5DK', '5LDK', 'House', 'Apartment', 'Mansion', 'Other'
  ];

  // Japan coordinate bounds validation
  const validateCoordinates = (lat, lng) => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);

    // Japan bounds: roughly 24°N to 46°N, 123°E to 146°E
    return (
      latitude >= 24.0 && latitude <= 46.0 &&
      longitude >= 123.0 && longitude <= 146.0
    );
  };

  const handleCoordinateChange = (index, value) => {
    // Allow only numbers and decimal point
    if (!/^-?\d*\.?\d*$/.test(value)) return;

    const newCoordinates = [...formData.Coordinates];
    newCoordinates[index] = value;
    setFormData({ ...formData, Coordinates: newCoordinates });
  };

  const handleNumberInput = (field, value, isInteger = false) => {
    // For integer fields (floors), only allow whole numbers
    if (isInteger) {
      if (!/^\d*$/.test(value)) return;
    } else {
      // For decimal fields (areas), allow decimal numbers
      if (!/^\d*\.?\d*$/.test(value)) return;
    }

    setFormData({ ...formData, [field]: value });
  };

  // Main form submission (with images - same as AkiyaTweak)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate coordinates before submitting
      const [lat, lng] = formData.Coordinates;
      if (!validateCoordinates(lat, lng)) {
        alert('Please enter valid coordinates for Japan (Lat: 24-46, Lng: 123-146)');
        setLoading(false);
        return;
      }

      // Submit ALL data including images (same as AkiyaTweak pattern)
      await onUpdate(formData);
    } finally {
      setLoading(false);
    }
  };

  // Add image from URL
  const handleAddImage = () => {
    if (newImageUrl.trim()) {
      setFormData(prevState => ({
        ...prevState,
        Images: [...prevState.Images, newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  // Upload image file (EXACTLY like AkiyaTweak)
  const handleFileUpload = () => {
    if (!selectedFile) return;

    setUploadingFile(true);
    
    const formDataUpload = new FormData();
    formDataUpload.append('ID', listing.property_id);
    formDataUpload.append('image', selectedFile);

    // Using fetch instead of axios like AkiyaTweak pattern
    fetch('https://app.akiya2.com/addImageToListings', {
      method: 'POST',
      body: formDataUpload
    })
    .then(response => response.json())
    .then(response => {
      console.log('Upload response:', response);
      setFormData(prevState => ({
        ...prevState,
        Images: [...prevState.Images, "https://app.akiya2.com/" + response.image_path]
      }));
      setSelectedFile(null);
      // Clear the file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
      alert('Image uploaded successfully!');
      setUploadingFile(false);
    })
    .catch(error => {
      console.error('Upload error:', error);
      alert('There was an error uploading the image.');
      setUploadingFile(false);
    });
  };

  // Remove image (update formData.Images)
  const handleRemoveImage = (index) => {
    const updatedImages = formData.Images.filter((_, i) => i !== index);
    setFormData(prevState => ({ ...prevState, Images: updatedImages }));
  };

  // Move image (same as AkiyaTweak)
  const handleImageMove = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.Images.length) return;

    const updatedImages = [...formData.Images];
    const [movedImage] = updatedImages.splice(index, 1);
    updatedImages.splice(newIndex, 0, movedImage);

    setFormData(prevState => ({ ...prevState, Images: updatedImages }));
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const updatedImages = [...formData.Images];
    const draggedImage = updatedImages[draggedIndex];

    updatedImages.splice(draggedIndex, 1);
    updatedImages.splice(dropIndex, 0, draggedImage);

    setFormData(prevState => ({ ...prevState, Images: updatedImages }));
    setDraggedIndex(null);
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: 'fas fa-info-circle' },
    { id: 'details', label: 'Property Details', icon: 'fas fa-home' },
    { id: 'location', label: 'Location', icon: 'fas fa-map-marker-alt' },
    { id: 'images', label: 'Images', icon: 'fas fa-images' },
    { id: 'contact', label: 'Contact Info', icon: 'fas fa-contact-card' }
  ];

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] my-8 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-primary px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="dark-header-nospacing" style={{ color: 'var(--color-2)', margin: 0 }}>Edit Listing</h2>
            <p className="text-sm text-secondary mt-1">Property ID: {listing.property_id}</p>
          </div>
          <button
            onClick={onClose}
            className="text-secondary hover:opacity-70 transition-colors non-selectable"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-primary">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap non-selectable ${activeTab === tab.id
                  ? 'border-b-2 text-primary border-primary'
                  : 'text-secondary hover:text-primary'
                  }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-6">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Address *
                    </label>
                    <input
                      type="text"
                      value={formData.Address}
                      onChange={(e) => setFormData({ ...formData, Address: e.target.value })}
                      className="w-full p-3 form-input rounded-lg"
                      placeholder="Full address"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Prefecture (Read-only)
                    </label>
                    <input
                      type="text"
                      value={formData.Prefecture}
                      className="w-full p-3 form-input rounded-lg bg-gray-100 cursor-not-allowed"
                      readOnly
                    />
                    <p className="text-xs text-secondary mt-1">Prefecture cannot be changed after listing creation</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Municipality (Read-only)
                    </label>
                    <input
                      type="text"
                      value={formData.Municipality}
                      className="w-full p-3 form-input rounded-lg bg-gray-100 cursor-not-allowed"
                      readOnly
                    />
                    <p className="text-xs text-secondary mt-1">Municipality cannot be changed after listing creation</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Price *
                    </label>
                    <input
                      type="text"
                      value={formData.Price}
                      onChange={(e) => setFormData({ ...formData, Price: e.target.value })}
                      className="w-full p-3 form-input rounded-lg"
                      placeholder="e.g., 5,000,000"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Property Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Room Type
                    </label>
                    <select
                      value={formData.Room_Type}
                      onChange={(e) => setFormData({ ...formData, Room_Type: e.target.value })}
                      className="w-full p-3 form-input rounded-lg"
                    >
                      <option value="">Select Room Type</option>
                      {roomTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Year of Construction
                    </label>
                    <input
                      type="number"
                      value={formData.Year_of_Construction}
                      onChange={(e) => handleNumberInput('Year_of_Construction', e.target.value, true)}
                      className="w-full p-3 form-input rounded-lg"
                      placeholder="e.g., 1998"
                      min="1800"
                      max={new Date().getFullYear()}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      House Area (m²)
                    </label>
                    <input
                      type="text"
                      value={formData.Area_of_House}
                      onChange={(e) => handleNumberInput('Area_of_House', e.target.value)}
                      className="w-full p-3 form-input rounded-lg"
                      placeholder="e.g., 230.5"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Land Area (m²)
                    </label>
                    <input
                      type="text"
                      value={formData.Area_of_Land}
                      onChange={(e) => handleNumberInput('Area_of_Land', e.target.value)}
                      className="w-full p-3 form-input rounded-lg"
                      placeholder="e.g., 300.0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Number of Floors
                    </label>
                    <input
                      type="text"
                      value={formData.No_of_Floors}
                      onChange={(e) => handleNumberInput('No_of_Floors', e.target.value, true)}
                      className="w-full p-3 form-input rounded-lg"
                      placeholder="e.g., 2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Listing Type
                    </label>
                    <select
                      value={formData.Empty_or_Not}
                      onChange={(e) => setFormData({ ...formData, Empty_or_Not: e.target.value })}
                      className="w-full p-3 form-input rounded-lg"
                    >
                      <option value="Sale">For Sale</option>
                      <option value="Rent">For Rent</option>
                      <option value="Both">Sale or Rent</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Location Tab */}
            {activeTab === 'location' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Latitude *
                    </label>
                    <input
                      type="text"
                      value={formData.Coordinates[0] || ''}
                      onChange={(e) => handleCoordinateChange(0, e.target.value)}
                      className="w-full p-3 form-input rounded-lg"
                      placeholder="e.g., 35.6762"
                    />
                    <p className="text-xs text-secondary mt-1">Valid range for Japan: 24.0 - 46.0</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                      Longitude *
                    </label>
                    <input
                      type="text"
                      value={formData.Coordinates[1] || ''}
                      onChange={(e) => handleCoordinateChange(1, e.target.value)}
                      className="w-full p-3 form-input rounded-lg"
                      placeholder="e.g., 139.6503"
                    />
                    <p className="text-xs text-secondary mt-1">Valid range for Japan: 123.0 - 146.0</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                    <i className="fas fa-info-circle mr-2"></i>
                    Coordinate Guidelines
                  </h4>
                  <ul className="text-sm text-secondary space-y-1">
                    <li>• Use decimal degrees format (e.g., 35.6762, 139.6503)</li>
                    <li>• Coordinates must be within Japan's boundaries</li>
                    <li>• You can find coordinates using Google Maps (right-click → coordinates)</li>
                    <li>• Current coordinates: {formData.Coordinates[0]}, {formData.Coordinates[1]}</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Images Tab - Now using separate state and API calls */}
            {activeTab === 'images' && (
              <div className="space-y-6">
                {/* Current Images */}
                <div>
                  <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-2)' }}>
                    Property Images ({formData.Images.length})
                  </h3>
                  {formData.Images.length > 0 ? (
                    <div className="space-y-4">
                      {formData.Images.map((image, index) => (
                        <div
                          key={index}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, index)}
                          className="flex items-center gap-4 p-4 border border-primary rounded-lg hover:bg-gray-50 cursor-move"
                        >
                          <div className="flex-shrink-0">
                            <img
                              src={image}
                              alt={`Property ${index + 1}`}
                              className="w-20 h-20 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23f3f4f6"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%236b7280">No Image</text></svg>';
                              }}
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: 'var(--color-2)' }}>
                              Image #{index + 1}
                            </p>
                            <p className="text-xs text-secondary truncate">{image}</p>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleImageMove(index, 'up')}
                              disabled={index === 0}
                              className="p-2 text-primary hover:bg-primary hover:text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <i className="fas fa-arrow-up"></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleImageMove(index, 'down')}
                              disabled={index === formData.Images.length - 1}
                              className="p-2 text-primary hover:bg-primary hover:text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <i className="fas fa-arrow-down"></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="p-2 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-colors"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <i className="fas fa-images text-4xl text-secondary opacity-50 mb-4"></i>
                      <p className="text-secondary">No images added yet</p>
                    </div>
                  )}
                </div>

                {/* Add Images Section */}
                <div className="border-t border-primary pt-6 space-y-6">
                  {/* Upload from Local File */}
                  <div>
                    <h4 className="text-md font-medium mb-3" style={{ color: 'var(--color-2)' }}>
                      <i className="fas fa-upload mr-2"></i>
                      Upload from Device
                    </h4>
                    <div className="flex gap-3">
                      <input
                        id="file-upload"
                        type="file"
                        accept="image/*"
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                        className="flex-1 p-3 form-input rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={handleFileUpload}
                        disabled={!selectedFile || uploadingFile}
                        className="px-4 py-3 gradient-primary text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 non-selectable"
                      >
                        {uploadingFile ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-upload mr-2"></i>
                            Upload
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Add from URL */}
                  <div>
                    <h4 className="text-md font-medium mb-3" style={{ color: 'var(--color-2)' }}>
                      <i className="fas fa-link mr-2"></i>
                      Add from URL
                    </h4>
                    <div className="flex gap-3">
                      <input
                        type="url"
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                        className="flex-1 p-3 form-input rounded-lg"
                        placeholder="Enter image URL (https://...)"
                      />
                      <button
                        type="button"
                        onClick={handleAddImage}
                        disabled={!newImageUrl.trim()}
                        className="px-4 py-3 gradient-primary text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 non-selectable"
                      >
                        <i className="fas fa-plus mr-2"></i>
                        Add
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-secondary">
                      <i className="fas fa-info-circle mr-1"></i>
                      <strong>Tips:</strong> Drag images to reorder them. The first image will be the main thumbnail.
                      Images will be saved to database when you click "Update Listing".
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Info Tab */}
            {activeTab === 'contact' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                    Property Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-3 form-input rounded-lg"
                    rows="5"
                    placeholder="Describe the property, its features, location benefits, etc..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                    Contact Information *
                  </label>
                  <textarea
                    value={formData.Contact_Info}
                    onChange={(e) => setFormData({ ...formData, Contact_Info: e.target.value })}
                    className="w-full p-3 form-input rounded-lg"
                    rows="3"
                    placeholder="Phone number, email, preferred contact method..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-2)' }}>
                    Website Link
                  </label>
                  <input
                    type="url"
                    value={formData.Link_to_Website}
                    onChange={(e) => setFormData({ ...formData, Link_to_Website: e.target.value })}
                    className="w-full p-3 form-input rounded-lg"
                    placeholder="https://example.com"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 bg-white border-t border-primary px-6 py-4 flex justify-between items-center">
            <div className="text-sm text-secondary">
              <i className="fas fa-info-circle mr-1"></i>
              Changes will be reviewed by admin before going live
            </div>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 teal-bg rounded-lg teal-hover transition-colors non-selectable"
                style={{ color: 'var(--color-2)' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 gradient-primary text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 non-selectable"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Update Listing
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MyListingsPage;